import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAITimeline } from '@/hooks/ai';
import { TimelineEvent } from '@/api/ai-api';
import { Button } from '@/components/ui/Button';

export function PatientTimelinePage() {
  const navigate = useNavigate();
  const { mutateAsync: generate, isPending, data } = useAITimeline();

  const [patientId, setPatientId] = useState('');

  const handleGenerate = async () => {
    if (!patientId) return;
    await generate({ patientId });
  };

  return (
    <div className="container max-w-5xl py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Linha do Tempo do Paciente</h1>
          <p className="text-gray-500 mt-1">Agent 04 — Resumo longitudinal e detecção de padrões</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>Voltar</Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <label className="block text-xs font-medium text-gray-700 mb-1">Patient ID</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={patientId}
            onChange={e => setPatientId(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="UUID do paciente"
          />
          <Button onClick={handleGenerate} disabled={isPending || !patientId}>
            {isPending ? 'Gerando...' : 'Gerar Timeline'}
          </Button>
        </div>
      </div>

      {data && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <h2 className="font-semibold text-blue-900 mb-3">Padrões detectados ({data.patterns.length})</h2>
            {data.patterns.length === 0 ? (
              <p className="text-sm text-blue-700">Nenhum padrão clínico relevante identificado.</p>
            ) : (
              <ul className="space-y-2">
                {data.patterns.map((p, i) => (
                  <li key={i} className="text-sm text-blue-900 bg-white rounded p-2 border border-blue-100">
                    • {p}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {data.progressionNotes.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
              <h2 className="font-semibold text-purple-900 mb-3">Notas de progressão</h2>
              <ul className="space-y-2">
                {data.progressionNotes.map((n, i) => (
                  <li key={i} className="text-sm text-purple-900 bg-white rounded p-2 border border-purple-100">
                    • {n}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-900 mb-4">
              Eventos ({data.totalEvents})
            </h2>

            {data.events.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum evento registrado no histórico.</p>
            ) : (
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" />
                <div className="space-y-3">
                  {data.events.map((event, i) => (
                    <TimelineItem key={i} event={event} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineItem({ event }: { event: TimelineEvent }) {
  const date = new Date(event.date);
  const colors: Record<string, string> = {
    consultation: 'bg-blue-500',
    prescription: 'bg-green-500',
    diagnosis: 'bg-orange-500',
    exam: 'bg-purple-500',
    certificate: 'bg-pink-500',
    evolution: 'bg-teal-500',
    alert: 'bg-red-500',
  };

  return (
    <div className="relative pl-10">
      <div className={`absolute left-1.5 w-3 h-3 rounded-full ${colors[event.type] || 'bg-gray-500'}`} />
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium uppercase text-gray-600">{event.type}</span>
          <span className="text-xs text-gray-500">{date.toLocaleDateString('pt-BR')}</span>
        </div>
        <div className="text-sm font-medium text-gray-900">{event.title}</div>
        {event.description && (
          <div className="text-xs text-gray-600 mt-1">{event.description}</div>
        )}
      </div>
    </div>
  );
}